// Foods catalog read hook. Backs the FoodPickerDrawer's category-tabbed list
// and Log's search.
//
// Drains cursor pages into one response. Server-side search keeps interactive
// queries small; ID-scoped reads hydrate only foods referenced by today's logs.
import type {
  FavoriteFood,
  FavoriteFoodsEnvelope,
  FoodCategory,
  FoodType,
  ListFoodsResponse,
  RecentFoodsEnvelope,
} from '@onflow/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api.js';

export interface UseFoodsArgs {
  category?: FoodCategory;
  type?: FoodType;
  search?: string;
  ids?: readonly string[];
}

const PAGE_LIMIT = 500;

export function useFoodsQuery({ category, type, search, ids }: UseFoodsArgs = {}) {
  return useQuery<ListFoodsResponse>({
    queryKey: [
      'ai',
      'foods',
      {
        category: category ?? null,
        type: type ?? null,
        search: search ?? null,
        ids: ids ? [...ids].sort() : null,
      },
    ] as const,
    queryFn: async () => {
      if (ids?.length === 0) return { items: [], nextCursor: null };
      const items: ListFoodsResponse['items'] = [];
      let cursor: string | null = null;
      // Cap iterations defensively in case the API ever returns a non-null
      // nextCursor on an empty page. At PAGE_LIMIT=500 this allows up to
      // 25,000 catalog rows before the loop bails.
      for (let i = 0; i < 50; i++) {
        const params = new URLSearchParams();
        if (category) params.set('category', category);
        if (type) params.set('type', type);
        if (search) params.set('search', search);
        if (ids) params.set('ids', ids.join(','));
        params.set('limit', String(PAGE_LIMIT));
        if (cursor) params.set('cursor', cursor);
        const page: ListFoodsResponse = await api<ListFoodsResponse>(`/foods?${params.toString()}`);
        items.push(...page.items);
        if (!page.nextCursor) break;
        cursor = page.nextCursor;
      }
      return { items, nextCursor: null };
    },
    // Catalog rows rarely change after the seed. Keep them around for the
    // drawer's lifetime and across category-tab switches.
    staleTime: 5 * 60_000,
  });
}

export function useRecentFoods() {
  return useQuery({
    queryKey: ['foods', 'recent'],
    queryFn: () => api<RecentFoodsEnvelope>('/foods/recent'),
  });
}

export function useFavoriteFoods() {
  return useQuery({
    queryKey: ['foods', 'favorites'],
    queryFn: () => api<FavoriteFoodsEnvelope>('/foods/favorites'),
  });
}

export function useToggleFavoriteFood() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ foodId, favorite }: { foodId: string; favorite: boolean }) => {
      if (favorite) {
        await api<FavoriteFood>(`/foods/${foodId}/favorite`, { method: 'POST' });
      } else {
        await api<void>(`/foods/${foodId}/favorite`, { method: 'DELETE' });
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['foods', 'favorites'] }),
  });
}
